import type { BoardTone } from './BoardLook'

/**
 * Culoarea pe care o trimite API-ul, in tonul tablourilor de bord.
 *
 * Ecranele vechi nu trimit "ok / warn / crit": trimit un hexa, fiindca desenul
 * de pe care vin turna culoarea direct pe dala. Cele patru sunt aceleasi peste
 * tot in portal - le scrie chiar `dashboard.php` in capul lui, si le scrie la
 * fel API-ul care a preluat ecranele (`DbalCoreAppPagesSource::coreDashboard`):
 *
 *   #5FB644  verde     totul raspunde
 *   #E6AC23  galben    macar unul in avertisment
 *   #E35B5A  rosu      macar unul in pericol
 *   #777     gri       n-are ce masura (zero masini)
 *
 * Se CITESTE culoarea, nu se socoteste din nou din cifre. Daca maine regula se
 * schimba in API, ecranul o urmeaza singur; doua socoteli ale aceluiasi lucru se
 * departeaza una de alta si nimeni nu observa - vezi de ce exista
 * `tools/check_taxonomy.py`.
 *
 * Griul NU e o culoare de stare: aplicatia fara nicio masina nu merge nici bine
 * nici rau. `flat` lasa cifra in cerneala obisnuita, ca un card de catalog fara
 * prag. Si o culoare pe care n-o stim cade tot pe `flat` - un ecran care ghiceste
 * "rosu" dintr-un hexa necunoscut sperie degeaba.
 */
const TONE_BY_COLOR: Record<string, BoardTone> = {
  '#5fb644': 'ok',
  '#e6ac23': 'warn',
  '#e35b5a': 'crit',
  '#777': 'flat',
  '#777777': 'flat',
}

export function toneOfColor(color: string): BoardTone {
  return TONE_BY_COLOR[String(color || '').toLowerCase()] ?? 'flat'
}

/**
 * Aceeasi treaba, pentru ecranele care trimit numele CLASEI de panou.
 *
 * Applications Dashboard nu trimite un hexa, ci `panel-red` / `panel-yellow` /
 * `panel-green` - clasa cu care dala veche isi lua chenarul colorat din
 * `app.css` (`DbalCoreAppPagesSource::paintDevices` si `monitoredApplications`).
 * E acelasi lucru spus altfel: starea cea mai rea dintre serviciile urmarite.
 *
 * Se traduce aici, langa cealalta, si nu in ecran: cele doua ecrane vecine
 * vorbesc doua dialecte ale aceleiasi idei, si locul unde se face trecerea spre
 * limbajul tablourilor trebuie sa fie unul singur. Ce nu recunoastem cade pe
 * `flat` - un ecran care ghiceste "rosu" dintr-un nume necunoscut sperie
 * degeaba.
 */
const TONE_BY_PANEL: Record<string, BoardTone> = {
  'panel-red': 'crit',
  'panel-yellow': 'warn',
  'panel-green': 'ok',
}

export function toneOfPanel(panel: string): BoardTone {
  return TONE_BY_PANEL[String(panel || '').toLowerCase()] ?? 'flat'
}

/**
 * Tonul unei LISTE: cel mai rau dintre randurile ei.
 *
 * Cardul poarta o singura stare, si ea trebuie sa fie cea care cere atentie -
 * un grup in care o masina e cazuta nu e "verde fiindca restul merg". Lista
 * goala nu e nici buna nici rea: `flat`.
 */
export function worstTone(panels: readonly string[]): BoardTone {
  if (panels.some((p) => toneOfPanel(p) === 'crit')) return 'crit'
  if (panels.some((p) => toneOfPanel(p) === 'warn')) return 'warn'
  if (panels.some((p) => toneOfPanel(p) === 'ok')) return 'ok'
  return 'flat'
}
